import type {
  CacheConfig,
  CacheEntry,
  MemoryCacheEntry,
  CacheStats,
} from './cache.types.ts';
import { CacheDatabase } from './cache-database.ts';
import { compressData, decompressData } from './cache-compression.ts';

const DEFAULT_CONFIG: CacheConfig = {
  ttl: 60 * 60 * 1000,             // 60 minutes
  compressionThreshold: 50 * 1024,  // 50 KB
  cleanupInterval: 30_000,          // 30 seconds
  maxEntries: 200,
  dbName: 'DynamicWidgetCache',
  enableLogging: false,
};

export class CacheManager {
  private config: CacheConfig;
  private memoryCache: Map<string, MemoryCacheEntry>;
  private db: CacheDatabase | null = null;
  private indexedDBAvailable = false;
  private cleanupTimer: ReturnType<typeof setInterval> | null = null;
  private stats = { l1Hits: 0, l2Hits: 0, misses: 0 };
  private initPromise: Promise<void> | null = null;

  constructor(config?: Partial<CacheConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.memoryCache = new Map();
  }

  // ──── Initialization ────

  async initialize(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    try {
      this.db = new CacheDatabase(this.config.dbName);
      await this.db.open();
      this.indexedDBAvailable = true;
      this.log('IndexedDB initialized');
    } catch (err) {
      this.indexedDBAvailable = false;
      this.db = null;
      this.log('IndexedDB unavailable, memory-only mode', err);
    }

    this.startCleanup();
  }

  // ──── GET ────

  async get<T>(key: string): Promise<T | null> {
    await this.initialize();

    // L1: Memory
    const memEntry = this.memoryCache.get(key);
    if (memEntry && !this.isExpiredEntry(memEntry.timestamp, memEntry.ttl)) {
      this.stats.l1Hits++;
      this.log(`L1 HIT: ${key.slice(0, 20)}...`);
      return memEntry.data as T;
    }
    if (memEntry) this.memoryCache.delete(key);

    // L2: IndexedDB
    if (this.indexedDBAvailable && this.db) {
      try {
        const entry = await this.db.apiCache.get(key);
        if (entry && !this.isExpiredEntry(entry.timestamp, entry.ttl)) {
          const data = decompressData<T>(entry.data, entry.compressed);

          // Promote to L1 — preserve per-entry ttl
          this.memoryCache.set(key, {
            data,
            timestamp: entry.timestamp,
            size: entry.size,
            ...(entry.ttl !== undefined ? { ttl: entry.ttl } : {}),
          });

          this.stats.l2Hits++;
          this.log(
            `L2 HIT: ${key.slice(0, 20)}... (${entry.compressed ? 'compressed' : 'raw'}, ${formatBytes(entry.size)})`,
          );
          return data;
        }
        if (entry) await this.db.apiCache.delete(key);
      } catch (err) {
        this.log('L2 read error', err);
      }
    }

    this.stats.misses++;
    this.log(`MISS: ${key.slice(0, 20)}...`);
    return null;
  }

  // ──── SET ────

  async set<T>(key: string, data: T, ttlMs?: number): Promise<void> {
    await this.initialize();
    const now = Date.now();

    const { data: serialized, compressed, originalSize } = compressData(
      data,
      this.config.compressionThreshold,
    );

    // L1 — include per-entry ttl when provided
    this.memoryCache.set(key, {
      data,
      timestamp: now,
      size: originalSize,
      ...(ttlMs !== undefined ? { ttl: ttlMs } : {}),
    });

    // L2
    if (this.indexedDBAvailable && this.db) {
      try {
        const entry: CacheEntry = {
          key,
          data: serialized,
          timestamp: now,
          size: originalSize,
          compressed,
          ...(ttlMs !== undefined ? { ttl: ttlMs } : {}),
        };
        await this.db.apiCache.put(entry);
        this.log(
          `STORED: ${key.slice(0, 20)}... (${compressed ? 'compressed' : 'raw'}, ${formatBytes(originalSize)}, ttl=${ttlMs ?? 'global'}ms)`,
        );
      } catch (err) {
        this.log('L2 write error', err);
      }
    }
  }

  // ──── INVALIDATE / CLEAR ────

  async invalidate(key: string): Promise<void> {
    this.memoryCache.delete(key);
    if (this.indexedDBAvailable && this.db) {
      try { await this.db.apiCache.delete(key); } catch { /* ignore */ }
    }
  }

  async invalidateByPrefix(prefix: string): Promise<void> {
    for (const key of this.memoryCache.keys()) {
      if (key.startsWith(prefix)) this.memoryCache.delete(key);
    }
    if (this.indexedDBAvailable && this.db) {
      try {
        const keys = await this.db.apiCache
          .filter((entry) => entry.key.startsWith(prefix))
          .primaryKeys();
        if (keys.length > 0) {
          await this.db.apiCache.bulkDelete(keys);
          this.log(`invalidateByPrefix('${prefix}'): removed ${keys.length} entries`);
        }
      } catch (err) {
        this.log('invalidateByPrefix error', err);
      }
    }
  }

  async clear(): Promise<void> {
    this.memoryCache.clear();
    if (this.indexedDBAvailable && this.db) {
      try { await this.db.apiCache.clear(); } catch { /* ignore */ }
    }
    this.log('Cache cleared');
  }

  // ──── TTL ────

  private isExpiredEntry(timestamp: number, entryTtl?: number): boolean {
    return Date.now() - timestamp > (entryTtl ?? this.config.ttl);
  }

  // ──── AUTO-CLEANUP ────

  private startCleanup(): void {
    if (this.cleanupTimer) return;
    this.cleanupTimer = setInterval(() => {
      void this.runCleanup();
    }, this.config.cleanupInterval);
  }

  private async runCleanup(): Promise<void> {
    const now = Date.now();
    const globalCutoff = now - this.config.ttl;

    // L1: barrido completo respetando TTL por entrada
    for (const [key, entry] of this.memoryCache.entries()) {
      if (this.isExpiredEntry(entry.timestamp, entry.ttl)) this.memoryCache.delete(key);
    }

    if (!this.indexedDBAvailable || !this.db) return;

    try {
      // Pasada 2a: borrado rápido indexado para entradas que superaron el TTL global
      const expiredGlobal = await this.db.apiCache
        .where('timestamp')
        .below(globalCutoff)
        .delete();

      if (expiredGlobal > 0) {
        this.log(`Cleanup pass 1: removed ${expiredGlobal} globally-expired entries`);
      }

      // Pasada 2b: barrido de entradas con TTL corto que expiraron antes del global
      // Solo se escanean las entradas más recientes que el corte global (las anteriores ya se borraron)
      const candidates = await this.db.apiCache
        .where('timestamp')
        .aboveOrEqual(globalCutoff)
        .toArray();

      const earlyExpiredKeys: string[] = [];
      for (const entry of candidates) {
        if (entry.ttl !== undefined && now - entry.timestamp > entry.ttl) {
          earlyExpiredKeys.push(entry.key);
        }
      }
      if (earlyExpiredKeys.length > 0) {
        await this.db.apiCache.bulkDelete(earlyExpiredKeys);
        this.log(`Cleanup pass 2: removed ${earlyExpiredKeys.length} custom-TTL expired entries`);
      }

      // Aplicar maxEntries (evicción LRU)
      const totalCount = await this.db.apiCache.count();
      if (totalCount > this.config.maxEntries) {
        const excess = totalCount - this.config.maxEntries;
        const oldest = await this.db.apiCache
          .orderBy('timestamp')
          .limit(excess)
          .primaryKeys();
        await this.db.apiCache.bulkDelete(oldest);
        this.log(`Cleanup: evicted ${excess} oldest entries`);
      }
    } catch (err) {
      this.log('Cleanup error', err);
    }
  }

  // ──── STATS ────

  async getStats(): Promise<CacheStats> {
    let l2Entries = 0;
    let totalSize = 0;

    if (this.indexedDBAvailable && this.db) {
      try {
        l2Entries = await this.db.apiCache.count();
        const all = await this.db.apiCache.toArray();
        totalSize = all.reduce((sum, e) => sum + e.size, 0);
      } catch { /* ignore */ }
    }

    return {
      l1Entries: this.memoryCache.size,
      l2Entries,
      l1Hits: this.stats.l1Hits,
      l2Hits: this.stats.l2Hits,
      misses: this.stats.misses,
      totalSize,
      isIndexedDBAvailable: this.indexedDBAvailable,
    };
  }

  // ──── LIFECYCLE ────

  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.memoryCache.clear();
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  // ──── LOGGING ────

  private log(message: string, error?: unknown): void {
    if (!this.config.enableLogging) return;
    const prefix = '[Cache]';
    if (error) {
      console.warn(`${prefix} ${message}`, error);
    } else {
      console.log(`${prefix} ${message}`);
    }
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
